const assetFieldNames = new Set([
  'brandLogo',
  'profileImageUrl',
  'logoUrl',
  'cobrandLogoUrl',
  'captionsSrc',
  'faviconUrl',
  'heroImage',
  'heroVideo',
  'image',
  'logo',
  'partnerLogo',
  'partnerSecondaryLogo',
  'poster',
  'src',
  'video',
  'videoUrl',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type BuiltInGlobusJobAsset = 'bakery' | 'butcher' | 'fresh-counter';

const builtInGlobusJobAssetPattern = /^\/globus-(bakery|butcher|fresh-counter)-[a-z0-9]+\.(?:png|webp)(?:[?#].*)?$/iu;

const builtInGlobusJobAsset = (value: unknown): BuiltInGlobusJobAsset | undefined => {
  if (typeof value !== 'string') return undefined;
  const kind = builtInGlobusJobAssetPattern.exec(value)?.[1]?.toLowerCase();
  return kind === 'bakery' || kind === 'butcher' || kind === 'fresh-counter' ? kind : undefined;
};

const bundledAssetKey = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('/editor-assets/')) return undefined;
  const pathname = value.split(/[?#]/u, 1)[0] ?? value;
  let decodedPathname = pathname;
  try {
    decodedPathname = decodeURIComponent(pathname);
  } catch {
    // An invalid escape sequence is not a generated bundle URL and must remain editorial content.
    return undefined;
  }
  const filename = decodedPathname.slice(decodedPathname.lastIndexOf('/') + 1).normalize('NFC');
  const match = /^(.*)-[a-z0-9]{8}\.([a-z0-9]+)$/iu.exec(filename);
  const [, name, extension] = match ?? [];
  return name && extension
    ? `${name.toLocaleLowerCase('en-US')}.${extension.toLocaleLowerCase('en-US')}`
    : undefined;
};

// A previous server-side normalization could encounter browser-only asset imports as empty objects
// and persist those objects in otherwise string-valued media fields. Repair malformed asset values on
// every normalization: a current revision can still contain server-created `{}` values. Valid editor
// uploads and deliberately cleared (empty-string) media fields remain untouched.
export const repairInvalidEditorAssets = (target: unknown, defaults: unknown): void => {
  if (Array.isArray(target) && Array.isArray(defaults)) {
    target.forEach((entry, index) => repairInvalidEditorAssets(entry, defaults[index]));
    return;
  }
  if (!isRecord(target) || !isRecord(defaults)) return;

  for (const [key, fallback] of Object.entries(defaults)) {
    const current = target[key];
    if (assetFieldNames.has(key) && typeof fallback === 'string' && typeof current !== 'string') {
      target[key] = fallback;
      continue;
    }
    repairInvalidEditorAssets(current, fallback);
  }
};

// Bundle asset URLs contain a content hash. Published editor snapshots can therefore retain an old
// URL after any shipped image or video was optimized or rebuilt. Resolve generated bundle URLs by
// their stable filename and retain the three historic GLOBUS aliases. Custom uploads, external URLs
// and deliberately cleared values remain editorial content and are never replaced.
export const refreshBuiltInGlobusJobAssets = (target: unknown, defaults: unknown): void => {
  const currentBuildAssets = new Map<BuiltInGlobusJobAsset, string>();
  const currentBundledAssets = new Map<string, string>();
  const collectCurrentBuildAssets = (value: unknown, fieldName?: string): void => {
    if (typeof value === 'string' && fieldName && assetFieldNames.has(fieldName)) {
      const kind = builtInGlobusJobAsset(value);
      const key = bundledAssetKey(value);
      if (kind) currentBuildAssets.set(kind, value);
      if (key) currentBundledAssets.set(key, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(entry => collectCurrentBuildAssets(entry));
      return;
    }
    if (isRecord(value)) {
      for (const [key, entry] of Object.entries(value)) collectCurrentBuildAssets(entry, key);
    }
  };
  collectCurrentBuildAssets(defaults);

  const refresh = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(refresh);
      return;
    }
    if (!isRecord(value)) return;
    for (const [key, current] of Object.entries(value)) {
      const isAsset = assetFieldNames.has(key);
      const kind = isAsset ? builtInGlobusJobAsset(current) : undefined;
      const assetKey = isAsset ? bundledAssetKey(current) : undefined;
      const replacement = (kind ? currentBuildAssets.get(kind) : undefined) ??
        (assetKey ? currentBundledAssets.get(assetKey) : undefined);
      if (replacement && current !== replacement) value[key] = replacement;
      else refresh(current);
    }
  };
  refresh(target);
};
